import React, { useEffect, useState, FormEvent } from 'react';
import axios from 'axios';
import {
    Button,
    Table,
    Group,
    Notification,
    LoadingOverlay,
    Stack,
    Title,
    Flex,
    Box,
    Text, Card, Avatar
} from '@mantine/core';
import { getToken } from "../../auth/service/authService";
import { BackendUrl } from "../../util/config";
import {IconFile, IconPhoto, IconUpload, IconX} from "@tabler/icons-react";
import {Dropzone} from "@mantine/dropzone";

interface TemplateType {
    id: number;
    name: string;
    pdfData?: string; // not typically sent in a listing, but included if your backend returns it
}

export function TemplateManager() {
    const [templates, setTemplates] = useState<TemplateType[]>([]);
    const [file, setFile] = useState<File | null>(null);

    // UI states
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState<string | null>(null);

    // Fetch templates on mount
    useEffect(() => {
        fetchTemplates();
    }, []);

    const fetchTemplates = async () => {
        try {
            const res = await axios.get<TemplateType[]>(BackendUrl + '/templates', {
                headers: {
                    Authorization: "Bearer " + getToken(),
                },
            });
            setTemplates(res.data);
        } catch (err) {
            console.error('Error fetching templates:', err);
        }
    };

    const handleDelete = async (id: number) => {
        await axios.delete<TemplateType>(BackendUrl + `/templates/${id}`, {
            headers: {
                Authorization: "Bearer " + getToken(),
            },
        });
        fetchTemplates();
    };

    // Handle form submission to upload a new template
    const handleUpload = async (e: FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        if (!file) {
            setError('Füge bitte eine Datei hinzu');
            return;
        }

        // Validate file type (must be PDF)
        if (file.type !== 'application/pdf' && file.type !== 'application/vnd.openxmlformats-officedocument.wordprocessingml.document') {
            setError('Only PDF and Docx files are allowed');
            return;
        }

        // Validate file size (max 10MB)
        const maxSizeInBytes = 10 * 1024 * 1024; // 10MB
        if (file.size > maxSizeInBytes) {
            setError('File size must not exceed 10MB');
            return;
        }

        setLoading(true);
        setError(null);
        setSuccess(null);

        try {
            const formData = new FormData();
            formData.append('name', file.name);
            formData.append('file', file); // key must match @RequestParam("file") in the backend

            const response = await axios.post<TemplateType>(BackendUrl + '/templates', formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                    Authorization: "Bearer " + getToken(),
                },
            });

            setSuccess(`Vorlage "${response.data.name}" erfolgreich hochgeladen!`);
            setFile(null);

            // Refresh the list
            fetchTemplates();
        } catch (err: any) {
            console.error('Upload error:', err);
            setError(err?.response?.data?.message || 'Fehler beim hochladen aufgetreten');
        } finally {
            setLoading(false);
        }
    };

    const handleDownload = async (documentId: number, fileName: string) => {
        try {
            // Download document as Blob
            const blobData = await downloadDocument(documentId);

            // Determine MIME type and extension based on the file name or blob's type
            let mimeType = blobData.type;
            let extension = '';

            // Check if the fileName already has a valid extension
            if (fileName.toLowerCase().endsWith('.pdf')) {
                extension = '.pdf';
                mimeType = 'application/pdf';
            } else if (fileName.toLowerCase().endsWith('.docx')) {
                extension = '.docx';
                mimeType = 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
            } else {
                // If no extension is present, check the blob's type
                if (blobData.type === 'application/pdf') {
                    extension = '.pdf';
                } else if (
                    blobData.type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
                ) {
                    extension = '.docx';
                } else {
                    // Fallback to PDF if type is not recognized
                    extension = '.pdf';
                    mimeType = 'application/pdf';
                }
                fileName = fileName + extension;
            }

            // Create a new Blob with the correct MIME type
            const fileBlob = new Blob([blobData], { type: mimeType });
            const url = window.URL.createObjectURL(fileBlob);

            // Create an anchor element to trigger the download
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', fileName);
            document.body.appendChild(link);
            link.click();

            // Cleanup the DOM and URL object
            link.parentNode?.removeChild(link);
            window.URL.revokeObjectURL(url);
        } catch (err) {
            console.error(err);
            alert('Failed to download the document.');
        }
    };


    const downloadDocument = async (templateId: number): Promise<Blob> => {
        const response = await axios.get(BackendUrl + `/templates/${templateId}/pdf`, {
            headers: {
                Authorization: "Bearer " + getToken(),
            },
            responseType: 'blob',
        });
        return response.data;
    };

    const handleDrop = (files: File[]) => {
        if (files.length > 0) {
            setFile(files[0]);
        }
    };

    const handleDeleteFile = () => {
        setFile(null);
    };


    return (
        <Box>
            {/* Overlay for loading */}
            <LoadingOverlay visible={loading} />

            <Flex direction={"row"} align={"center"} gap={"15px"} mb="md">
                <Title order={2} >
                    Vorlagen Management
                </Title>
                <Text>*Hier hochgeladene Dokumente werden jedem Nutzer angezeigt</Text>
            </Flex>


            {/* Error message */}
            {error && (
                <Notification color="red" onClose={() => setError(null)} mb="md">
                    {error}
                </Notification>
            )}

            {/* Success message */}
            {success && (
                <Notification color="teal" onClose={() => setSuccess(null)} mb="md">
                    {success}
                </Notification>
            )}

            {/* Upload form */}
            <form onSubmit={handleUpload}>
                <Stack gap="sm" mb="md">
                    <Dropzone
                        onDrop={handleDrop}
                        onReject={(files) => console.log('rejected files', files)}
                        maxSize={10 * 1024 ** 2}
                        accept={["application/pdf", "application/vnd.openxmlformats-officedocument.wordprocessingml.document"]}
                        multiple={false}
                    >
                        <Group justify="center" gap="xl" mih={220} style={{ pointerEvents: 'none' }}>
                            <Dropzone.Accept>
                                <IconUpload size={52} color="var(--mantine-color-blue-6)" stroke={1.5} />
                            </Dropzone.Accept>
                            <Dropzone.Reject>
                                <IconX size={52} color="var(--mantine-color-red-6)" stroke={1.5} />
                            </Dropzone.Reject>
                            <Dropzone.Idle>
                                <IconPhoto size={52} color="var(--mantine-color-dimmed)" stroke={1.5} />
                            </Dropzone.Idle>

                            <div>
                                <Text size="xl" inline>
                                    Ziehe das PDF oder Docx hierhin oder klicke um Datei auszuwählen
                                </Text>
                                <Text size="sm" c="dimmed" inline mt={7}>
                                    Füge eine PDF oder Docx Datei hinzu, die nicht 10MB überschreitet
                                </Text>
                            </div>
                        </Group>
                    </Dropzone>

                    {file && (
                        <Box mt="md">
                            <Card withBorder shadow="sm" padding="md">
                                <Group align="center">
                                    <Avatar size="lg" radius="sm" variant="light">
                                        <IconFile size={24} />
                                    </Avatar>
                                    <div style={{ flex: 1, marginLeft: 10 }}>
                                        <Text w={500}>{file.name}</Text>
                                    </div>
                                    <Button variant="outline" onClick={handleDeleteFile}>
                                        Datei nicht mehr auswählen
                                    </Button>
                                </Group>
                            </Card>
                        </Box>
                    )}
                </Stack>
                <Group p="right">
                    <Button variant="filled" type="submit">Hochladen</Button>
                </Group>

            </form>

            <Title order={3} mt="xl" mb="md">
                Bereits hochgeladene Vorlagen
            </Title>

            {templates.length === 0 ? (
                <Text>Keine Vorlagen gefunden</Text>
            ) : (
                <Table>
                    <Table.Thead>
                        <Table.Tr>
                            <Table.Th>ID</Table.Th>
                            <Table.Th>Name</Table.Th>
                            <Table.Th>Datei</Table.Th>
                        </Table.Tr>
                    </Table.Thead>
                    <Table.Tbody>
                        {templates.map((t) => (
                            <Table.Tr key={t.id}>
                                <Table.Td>{t.id}</Table.Td>
                                <Table.Td>{t.name} {}</Table.Td>
                                <Table.Td>
                                    <Flex direction="row" align="center" gap="sm">
                                        {/* Download link: calls GET /api/templates/{id}/pdf */}
                                        <Button
                                            variant="filled"
                                            onClick={() => handleDownload(t.id, t.name)}
                                        >
                                            herunterladen
                                        </Button>
                                        <Button
                                            variant="light"
                                            onClick={() => handleDelete(t.id)}
                                        >
                                            löschen
                                        </Button>
                                    </Flex>
                                </Table.Td>
                            </Table.Tr>
                        ))}
                    </Table.Tbody>
                </Table>
            )}
        </Box>
    );
}
